import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { CalendarClock, Mail, History, CloudOff } from "lucide-react";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

function shareText(d) {
  return [
    "*AnimalMela — Daily Haat Summary*",
    `*${d.haat_name || ""}* — ${d.date}`,
    "",
    `Collections: ${inr(d.total_collected)} (${d.receipts} receipts)`,
    `Animals in: ${d.animals_in}`,
    `Exits: ${d.exits_sold} sold · ${d.exits_unsold} unsold`,
    `Still in haat: ${d.still_in_haat}`,
    `New sellers: ${d.sellers_added} · New purchasers: ${d.purchasers_added}`,
    `Pending offline sync: ${d.pending_sync}`,
    "",
    "www.animalmela.com",
  ].join("\n");
}

const shareOnWhatsApp = (d) =>
  window.open(`https://wa.me/?text=${encodeURIComponent(shareText(d))}`, "_blank", "noopener");

const Tile = ({ label, value, warn = false }) => (
  <div className={`rounded-2xl p-3 ${warn ? "bg-amber-50 border border-amber-200" : "bg-muted/40"}`}>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
    <div className="font-display text-xl font-extrabold mt-0.5">{value}</div>
  </div>
);

export default function DailySummaryCard() {
  const { t } = useT();
  const [d, setD] = useState(null);
  const [histOpen, setHistOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [mailing, setMailing] = useState(false);

  useEffect(() => { api.get("/subscriber/digest/today").then((r) => setD(r.data)).catch(() => {}); }, []);

  const openHistory = () => {
    setHistOpen(true);
    api.get("/subscriber/digests").then((r) => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };

  const emailNow = async () => {
    setMailing(true);
    try {
      const r = await api.post(`/subscriber/digests/${d.date}/email`);
      toast.success(`${t("digest.emailSent")}: ${r.data.emailed_to}`);
      setD((x) => ({ ...x, email_status: "sent", finalized: true }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("digest.emailFailed"));
    } finally { setMailing(false); }
  };

  if (!d) return null;

  return (
    <div className="am-card p-6 mt-6" data-testid="daily-summary-card">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-2xl bg-accent grid place-items-center shrink-0">
          <CalendarClock className="w-5 h-5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-bold leading-tight">{t("digest.title")}</h2>
          <p className="text-xs text-muted-foreground">
            {d.date} · {d.finalized
              ? t("digest.finalized")
              : t("digest.livePreview", { time: d.closing_time })}
            {d.email_status === "sent" && ` · ${t("digest.emailSent")}`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-full ring-1 ring-[#25D366]/30 text-[#0f6b3a] hover:text-[#0f6b3a]"
            onClick={() => shareOnWhatsApp(d)} data-testid="digest-share-btn">
            <WhatsAppIcon className="w-4 h-4 mr-1.5" color="#25D366" /> WhatsApp
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={emailNow} disabled={mailing} data-testid="digest-email-btn">
            <Mail className="w-4 h-4 mr-1.5" /> {t("digest.emailNow")}
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={openHistory} data-testid="digest-history-btn">
            <History className="w-4 h-4 mr-1.5" /> {t("digest.history")}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Tile label={t("digest.collections")} value={`${inr(d.total_collected)}`} />
        <Tile label={t("digest.animalsIn")} value={d.animals_in} />
        <Tile label={t("digest.exitsSold")} value={d.exits_sold} />
        <Tile label={t("digest.exitsUnsold")} value={d.exits_unsold} />
        <Tile label={t("digest.stillInHaat")} value={d.still_in_haat} />
        <Tile label={t("digest.newSellers")} value={d.sellers_added} />
        <Tile label={t("digest.newPurchasers")} value={d.purchasers_added} />
        <Tile label={t("digest.pendingSync")} value={d.pending_sync} warn={d.pending_sync > 0} />
      </div>
      {d.pending_sync > 0 && (
        <div className="mt-3 text-xs text-amber-800 flex items-center gap-1.5">
          <CloudOff className="w-3.5 h-3.5" /> {t("digest.pendingSyncHint", { n: d.pending_sync, devices: d.pending_sync_devices })}
        </div>
      )}

      <Dialog open={histOpen} onOpenChange={setHistOpen}>
        <DialogContent className="max-w-lg" data-testid="digest-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{t("digest.title")} — {t("digest.history")}</DialogTitle>
            <DialogDescription>{t("digest.historyHint")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto space-y-2">
            {history.map((h) => (
              <div key={h.date} className="flex items-center gap-3 rounded-2xl border p-3" data-testid={`digest-row-${h.date}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{h.date}</div>
                  <div className="text-xs text-muted-foreground">
                    {inr(h.total_collected)} · {h.receipts} {t("digest.receipts")} · {h.animals_in} {t("sub.animalsCount")}
                  </div>
                </div>
                {h.email_status === "sent" && <span className="am-chip am-chip-ok text-[10px]">{t("digest.emailSent")}</span>}
                <Button size="icon" variant="outline" className="rounded-full w-8 h-8 shrink-0" onClick={() => shareOnWhatsApp(h)} data-testid={`digest-reshare-${h.date}`}>
                  <WhatsAppIcon className="w-4 h-4" color="#25D366" />
                </Button>
              </div>
            ))}
            {history.length === 0 && <div className="py-8 text-center text-sm text-muted-foreground">{t("sub.noData")}</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
